import React from 'react'
import { Box, Typography, Avatar } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { ParticipantsContainerMode } from '../ParticipantsContainer/ParticipantsContainer'

export interface Participant {
  id: string
  name: string
  email: string
}

export interface ParticipantBoxInfoProps {
  participant: Participant
  onRemove: (id: string) => void
  isDragging?: boolean
  className?: string
  showRemoveButton?: boolean
  hasError?: boolean
  mode?: ParticipantsContainerMode
}

export const ParticipantBoxInfo: React.FC<ParticipantBoxInfoProps> = ({
  participant,
  onRemove,
  isDragging: _isDragging = false,
  className = '',
  showRemoveButton = true,
  hasError = false,
  mode
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isDraggingFromHook
  } = useDraggable({
    id: participant.id,
    data: {
      type: 'participant',
      participant
    },
    disabled: mode === 'preview' // Disable drag in preview mode
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDraggingFromHook ? 0.5 : 1
  }
  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onRemove(participant.id)
  }

  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Draggable participant card */}
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: hasError ? INTERAX_COLORS.redError : INTERAX_COLORS.heritageBlue,
          backgroundColor: hasError ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.heritageBlue,
          transition: 'all 0.2s',
          cursor: mode === 'preview' ? 'default' : 'move',
          flex: 1,
          ...style
        }}
      >
        {/* Avatar with initials */}
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: INTERAX_COLORS.sun,
            color: INTERAX_COLORS.offBlack,
            fontSize: '0.875rem',
            fontWeight: 600,
            flexShrink: 0
          }}
        >
          {getInitials(participant.name)}
        </Avatar>

        {/* Participant info */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography
            className="text-body-2"
            sx={{
              color: INTERAX_COLORS.white,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={participant.name}
          >
            {participant.name}
          </Typography>
          <Typography
            className="text-body-3"
            sx={{
              color: INTERAX_COLORS.thunderGrey,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={participant.email}
          >
            {participant.email}
          </Typography>
        </Box>
      </Box>

      {/* Remove button - outside the draggable area */}
      {showRemoveButton && (
        <Box
          component="button"
          onClick={handleRemove}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: INTERAX_COLORS.redErrorBackground,
            border: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            flexShrink: 0,
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <XMarkIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.redError }} />
        </Box>
      )}
    </Box>
  )
}
